import React, { useMemo, useState } from "react";
import { v4 as uuidV4 } from "uuid";
import { useQueryClient } from "@tanstack/react-query";
import { AlertCircle, CheckCircle2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { useToast } from "@/components/ui/use-toast";
import {
  BulkSendInstancesValidation,
  PurchaseReleaseBreakdownItem,
  ListCfrTemplateItem,
  useCreateBulkInstances,
  useListCfrTemplates,
} from "@/lib/api/cfr";
import { chunkArray } from "@/lib/prelude";

interface BulkPurchaseReleaseBreakdownSendDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedItems: PurchaseReleaseBreakdownItem[];
}

export function BulkPurchaseReleaseBreakdownSendDialog({
  open,
  onOpenChange,
  selectedItems,
}: BulkPurchaseReleaseBreakdownSendDialogProps) {
  const { toast } = useToast();
  const createBulkInstances = useCreateBulkInstances();
  const queryClient = useQueryClient();
  const templatesResult = useListCfrTemplates();
  const templateData: ListCfrTemplateItem[] = templatesResult.data?.data?.data || [];

  const [isTestSend, setIsTestSend] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentBatch, setCurrentBatch] = useState(0);
  const [totalBatches, setTotalBatches] = useState(0);
  const [successfulInstances, setSuccessfulInstances] = useState(0);
  const [failedInstances, setFailedInstances] = useState(0);

  const templateLookup = useMemo(() => {
    const map = new Map<string, string>();
    for (const template of templateData) {
      if (template.id && template.DocumentTemplate?.id) {
        map.set(template.id, template.DocumentTemplate.id);
      }
    }
    return map;
  }, [templateData]);

  const validationSummary = useMemo(() => {
    const validItems: Array<{ item: PurchaseReleaseBreakdownItem; templateId: string }> = [];
    const missingCustomer: PurchaseReleaseBreakdownItem[] = [];
    const missingTemplate: PurchaseReleaseBreakdownItem[] = [];

    for (const item of selectedItems) {
      const hasCustomer = !!item.customer_id;
      const templateId = item.document_template_id || templateLookup.get(item.id) || null;
      const hasTemplate = !!templateId;

      if (hasCustomer && hasTemplate) {
        validItems.push({ item, templateId: templateId! });
        continue;
      }

      if (!hasCustomer) {
        missingCustomer.push(item);
      }
      if (!hasTemplate) {
        missingTemplate.push(item);
      }
    }

    return {
      validItems,
      missingCustomer,
      missingTemplate,
    };
  }, [selectedItems, templateLookup]);

  const commoditySummary = useMemo(() => {
    const summaryMap = new Map<string, number>();
    for (const item of selectedItems) {
      const key = `${item.commodity_type || "unknown"}|${item.crop_year ?? "n/a"}`;
      summaryMap.set(key, (summaryMap.get(key) || 0) + 1);
    }
    return Array.from(summaryMap.entries()).map(([key, count]) => {
      const [commodity, cropYear] = key.split("|");
      return { commodity, cropYear, count };
    });
  }, [selectedItems]);

  const resetProgress = () => {
    setIsSubmitting(false);
    setCurrentBatch(0);
    setTotalBatches(0);
    setSuccessfulInstances(0);
    setFailedInstances(0);
  };

  const handleClose = (openState: boolean) => {
    if (!openState) {
      resetProgress();
      setIsTestSend(false);
    }
    onOpenChange(openState);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (isSubmitting) return;

    if (validationSummary.validItems.length === 0) {
      toast({
        title: "Cannot Send Contracts",
        description: "No selected items are eligible for sending.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    setSuccessfulInstances(0);
    setFailedInstances(0);

    try {
      const BATCH_SIZE = 10;
      const batches = chunkArray(BATCH_SIZE, validationSummary.validItems);
      setTotalBatches(batches.length);

      let totalSuccessful = 0;
      let totalFailed = 0;

      for (let batchIndex = 0; batchIndex < batches.length; batchIndex++) {
        const batch = batches[batchIndex];
        setCurrentBatch(batchIndex + 1);

        try {
          const to_send = batch.map(({ item, templateId }) => ({
            cfr_document_id: item.id,
            group_id: uuidV4(),
            template_id: templateId,
            customer_id: item.customer_id!,
            is_test_send: isTestSend,
            is_draft: false,
          }));

          const parseResult = BulkSendInstancesValidation.safeParse({ to_send });

          if (!parseResult.success) {
            console.error("Failed to validate bulk send data for batch", batchIndex + 1, parseResult.error);
            totalFailed += to_send.length;
            setFailedInstances(totalFailed);
            continue;
          }

          const result = await createBulkInstances(parseResult.data);

          if (result.success) {
            totalSuccessful += to_send.length;
            setSuccessfulInstances(totalSuccessful);
          } else {
            console.error("Failed to send batch", batchIndex + 1, result);
            totalFailed += to_send.length;
            setFailedInstances(totalFailed);
          }
        } catch (error) {
          console.error("Error processing batch", batchIndex + 1, error);
          const batchCount = batch.length;
          totalFailed += batchCount;
          setFailedInstances(totalFailed);
        }
      }

      await queryClient.invalidateQueries({ queryKey: ["cfr"] });

      if (totalFailed === 0) {
        toast({
          title: "Bulk Send Completed",
          description: `Successfully queued ${totalSuccessful} contract(s).`,
        });
      } else if (totalSuccessful === 0) {
        toast({
          title: "Bulk Send Failed",
          description: `Failed to queue all ${totalFailed} contract(s).`,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Bulk Send Partially Completed",
          description: `Queued ${totalSuccessful} contract(s). ${totalFailed} failed.`,
          variant: "destructive",
        });
      }

      if (totalSuccessful > 0) {
        handleClose(false);
      }
    } catch (error) {
      console.error("Failed to queue bulk instances", error);
      const errorMessage = error instanceof Error ? error.message : "Failed to queue bulk instances";
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle>Bulk Send Purchase Release Contracts</DialogTitle>
        </DialogHeader>

        {isSubmitting ? (
          <div className="grid gap-4 py-8">
            <div className="text-center space-y-4">
              <div className="text-lg font-semibold">Sending Contracts...</div>

              <div className="space-y-2">
                <div className="text-sm text-muted-foreground">
                  Processing batch {currentBatch} of {totalBatches}
                </div>

                <div className="w-full bg-gray-200 rounded-full h-2.5">
                  <div
                    className="bg-blue-600 h-2.5 rounded-full transition-all duration-300"
                    style={{
                      width: `${totalBatches > 0 ? (currentBatch / totalBatches) * 100 : 0}%`,
                    }}
                  ></div>
                </div>

                <div className="text-sm text-muted-foreground">
                  {totalBatches > 0 ? Math.round((currentBatch / totalBatches) * 100) : 0}% complete
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 pt-4">
                <div className="text-center p-3 bg-green-50 rounded-lg">
                  <div className="text-sm text-muted-foreground">Successful</div>
                  <div className="text-2xl font-bold text-green-600">{successfulInstances}</div>
                </div>
                <div className="text-center p-3 bg-red-50 rounded-lg">
                  <div className="text-sm text-muted-foreground">Failed</div>
                  <div className="text-2xl font-bold text-red-600">{failedInstances}</div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label>Selected Contracts</Label>
                <div className="text-sm text-muted-foreground">
                  {selectedItems.length} item(s) selected
                </div>
              </div>

              <div className="grid gap-2">
                <Label htmlFor="is_test_send">Is Test Send</Label>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="is_test_send"
                    checked={isTestSend}
                    onCheckedChange={(checked) => setIsTestSend(!!checked)}
                  />
                </div>
              </div>

              <div className="grid gap-2">
                <Label>Validation Summary</Label>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                    <span className="font-medium text-green-600">
                      {validationSummary.validItems.length} ready to send
                    </span>
                  </div>
                  {validationSummary.missingCustomer.length > 0 && (
                    <div className="flex items-start gap-2">
                      <AlertCircle className="h-4 w-4 text-yellow-600 mt-0.5" />
                      <span className="text-yellow-700">
                        {validationSummary.missingCustomer.length} missing customer assignment
                      </span>
                    </div>
                  )}
                  {validationSummary.missingTemplate.length > 0 && (
                    <div className="flex items-start gap-2">
                      <AlertCircle className="h-4 w-4 text-red-600 mt-0.5" />
                      <span className="text-red-700">
                        {validationSummary.missingTemplate.length} missing template metadata
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {commoditySummary.length > 0 && (
                <div className="grid gap-2">
                  <Label>Commodity &amp; Crop Year Mix</Label>
                  <div className="grid gap-1 text-sm text-muted-foreground">
                    {commoditySummary.map(({ commodity, cropYear, count }) => (
                      <div key={`${commodity}-${cropYear}`} className="flex justify-between">
                        <span>{`${commodity.toUpperCase()} • ${cropYear}`}</span>
                        <span>{count} item(s)</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => handleClose(false)}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={validationSummary.validItems.length === 0 || isSubmitting}
              >
                {validationSummary.validItems.length === 0
                  ? "No Sendable Contracts"
                  : `Send ${validationSummary.validItems.length} Contract(s)`}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

